import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Search, ArrowRight, Home, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Antibodies', 'Cell Culture Media', 'PCR Reagents', 'ELISA Kits',
  'Molecular Biology', 'Biochemicals', 'Lab Plastics', 'Safety Equipment',
  'Solvents & Chemicals', 'Microscopy', 'Flow Cytometry', 'Protein Assay',
  'RNA Extraction', 'Enzyme Assays', 'Staining & Dyes',
];

// UUID pattern: 8-4-4-4-12 hex chars
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Any slug that is mostly hex chars (internal DB IDs like MongoDB ObjectIds)
const HEX_ID_RE = /^[0-9a-f]{20,}$/i;

function parseSlug(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  const raw = segments[segments.length - 1] || '';

  // If it's a UUID or a raw hex ID, don't use it as a search query
  if (UUID_RE.test(raw) || HEX_ID_RE.test(raw)) {
    return { brand: '', query: '' };
  }

  const words = raw.replace(/[-_]/g, ' ').replace(/\d{6,}/g, '').trim();
  const parts = words.split(' ').filter(Boolean);
  const brand = parts[0] || '';
  const query = words;
  return { brand, query };
}

export default function NotFound() {
  const location = useLocation();

  const { query: detectedQuery } = parseSlug(location.pathname);

  const [query, setQuery] = useState(detectedQuery);
  const [brands, setBrands] = useState([]);

  // Signal to Google: do not index this page
  useEffect(() => {
    const metaRobots = document.querySelector('meta[name="robots"]');
    const prev = metaRobots ? metaRobots.getAttribute('content') : null;
    if (metaRobots) metaRobots.setAttribute('content', 'noindex, follow');
    else {
      const m = document.createElement('meta');
      m.name = 'robots'; m.content = 'noindex, follow';
      document.head.appendChild(m);
    }
    const prevTitle = document.title;
    document.title = 'Product Not Found | BioMall.ai';
    return () => {
      // Restore on unmount so navigating away doesn't persist noindex
      if (metaRobots && prev) metaRobots.setAttribute('content', prev);
      document.title = prevTitle;
    };
  }, []);

  // Fetch partner brands for the bottom section
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await fetch(`${API}/api/brands?partner_only=true`);
        if (res.ok) {
          const data = await res.json();
          setBrands((data.brands || []).slice(0, 12));
        }
      } catch (_) {}
    };
    fetchBrands();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q) window.location.href = `/?q=${encodeURIComponent(q)}`;
    else window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Dark 404 hero section */}
      <div className="bg-[#0f172a] flex-1 flex flex-col items-center justify-center px-4 py-16 text-center">

        {/* Badge */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-400 text-xs font-bold uppercase tracking-widest mb-6">
          <Zap className="w-3 h-3" />
          Page Not Found
        </div>

        {/* 404 number */}
        <div className="text-8xl sm:text-9xl font-black text-white mb-4 leading-none">
          404
        </div>

        {/* Heading */}
        <h1 className="text-xl sm:text-2xl font-bold text-white mb-3">
          This product page hasn't migrated yet.
        </h1>
        <p className="text-sm text-slate-400 max-w-lg mb-8">
          We're migrating thousands of products from{' '}
          <span className="text-slate-300 font-semibold">biomall.in</span> to{' '}
          <span className="text-blue-400 font-semibold">biomall.ai</span>. This particular page
          isn't live yet — but you can search for it below or browse our partner brands.
        </p>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="w-full max-w-lg flex gap-2 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="notfound-search-input"
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search products, kits, reagents…"
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-800 border border-slate-600 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <button
            data-testid="notfound-search-btn"
            type="submit"
            className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded-xl transition-colors whitespace-nowrap"
          >
            Search
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        {/* Back home link */}
        <Link
          data-testid="notfound-home-link"
          to="/"
          className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
        >
          <Home className="w-4 h-4" />
          Back to BioMall.ai homepage
        </Link>
      </div>

      {/* Browse by category */}
      <div className="bg-white px-4 py-12 text-center">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">
          Browse by Category
        </p>
        <div className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
          {CATEGORIES.map(cat => (
            <Link
              key={cat}
              to={`/search?q=${encodeURIComponent(cat)}`}
              data-testid={`notfound-category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
            >
              {cat}
            </Link>
          ))}
        </div>
      </div>

      {/* Our partner brands */}
      {brands.length > 0 && (
        <div className="bg-gray-50 border-t border-gray-100 px-4 py-12 text-center">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6">
            Our Partner Brands
          </p>
          <div className="flex flex-wrap justify-center items-center gap-6 max-w-4xl mx-auto">
            {brands.map((b, i) => (
              <Link
                key={i}
                to={`/brands/${(b.slug || b.name || '').toLowerCase().replace(/\s+/g, '-')}`}
                data-testid={`notfound-brand-${i}`}
                className="opacity-60 hover:opacity-100 transition-opacity"
              >
                {b.logo_url ? (
                  <img src={b.logo_url} alt={b.name} className="h-8 object-contain max-w-[100px]" />
                ) : (
                  <span className="text-sm font-semibold text-gray-500">{b.name}</span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
